/**
 * Section scroll reveal (theme settings → Animations → Section reveal on scroll).
 *
 * Sections below the fold rise in as they scroll into view. With "Also reveal
 * cards individually" on, grids marked data-scroll-reveal-cards (in their
 * section's Liquid) reveal their direct children one by one instead, and the
 * host section stays static. Progressive enhancement, deliberately conservative:
 *
 * - Nothing is hidden until this module runs, and only sections whose top sits
 *   below the fold at that moment are hidden — first paint (hero / LCP) is never
 *   touched, and without JS the page is simply static.
 * - Once a section has revealed, every class and inline style is stripped so no
 *   transform lingers. A retained transform creates a containing block, which
 *   would break position: sticky inside the section (collection filter bar,
 *   PDP sticky column).
 * - Skipped for prefers-reduced-motion. In the theme editor the effect runs
 *   (scroll the preview to see it), but a section the editor re-renders or
 *   selects is neutralised so edits never replay entrances or hide content.
 *
 * Opt-out for a single section: add data-scroll-reveal-skip to the section
 * element (e.g. via a section's own Liquid).
 */

const HIDDEN_CLASS = 'scroll-reveal';
const IN_CLASS = 'scroll-reveal-in';

/* Fraction of the viewport height treated as "the fold" — sections whose top is
   above this line at init are left alone; the same line (as a rootMargin) is
   where scrolled-to sections start their reveal. */
const FOLD_LINE = 0.92;

/* Delay step between sections revealing in the same observer batch, so two short
   sections entering together cascade instead of moving as one slab. */
const STAGGER_MS = 80;

/* Delay step between cards in the same batch — a row of cards cascades
   left-to-right. Tighter than sections: rows have more members. */
const CARD_STAGGER_MS = 60;

/* Page-load entrance (data-scroll-reveal-load): delay step between above-fold
   targets, and the cap so a busy first viewport never waits long for its tail. */
const LOAD_STAGGER_MS = 120;
const LOAD_DELAY_MAX_MS = 600;

function cleanup(section) {
  section.classList.remove(HIDDEN_CLASS, IN_CLASS);
  section.style.removeProperty('transition-delay');
}

/**
 * @param {Element} section
 * @param {number} delayMs
 * @param {number} durationMs
 */
function reveal(section, delayMs, durationMs) {
  if (delayMs > 0) section.style.setProperty('transition-delay', `${delayMs}ms`);
  section.classList.add(IN_CLASS);

  const onEnd = (event) => {
    if (event.target !== section || event.propertyName !== 'transform') return;
    section.removeEventListener('transitionend', onEnd);
    clearTimeout(failsafe);
    cleanup(section);
  };
  section.addEventListener('transitionend', onEnd);

  /* transitionend is not guaranteed (display: none mid-flight, interrupted
     morphs) — never leave a section stuck at opacity 0. */
  const failsafe = setTimeout(() => {
    section.removeEventListener('transitionend', onEnd);
    cleanup(section);
  }, durationMs + delayMs + 300);
}

/** @type {IntersectionObserver | null} */
let observer = null;

function init() {
  if (!document.body.hasAttribute('data-scroll-reveal')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const sections = document.querySelectorAll('main.content-for-layout > .shopify-section');
  if (sections.length === 0) return;

  const durationMs =
    parseFloat(getComputedStyle(document.body).getPropertyValue('--scroll-reveal-duration')) || 600;

  const foldLine = window.innerHeight * FOLD_LINE;
  const cardsMode = document.body.hasAttribute('data-scroll-reveal-cards');
  const loadMode = document.body.hasAttribute('data-scroll-reveal-load');

  /* Card mode: containers marked data-scroll-reveal-cards contribute their
     direct children as individual reveal targets, and their host section stays
     static — a card rising inside a rising section would move double. */
  const grids = cardsMode
    ? [...document.querySelectorAll('main.content-for-layout [data-scroll-reveal-cards]')]
    : [];

  const scrollTargets = [];
  const loadTargets = [];

  /* Elements that aren't rendered (display: none — e.g. the responsive
     hidden--mobile / hidden--desktop duplicates of a grid) report a 0×0 rect at
     0,0. They must not join either pass: they'd sort to the front of the load
     cascade and eat its stagger slots invisibly. */
  const classify = (/** @type {Element} */ target) => {
    if (target.hasAttribute('data-scroll-reveal-skip')) return;
    const rect = target.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    if (rect.top > foldLine) scrollTargets.push(target);
    else if (loadMode) loadTargets.push(target);
  };

  for (const grid of grids) {
    for (const card of grid.children) classify(card);
  }

  for (const section of sections) {
    if (cardsMode && section.querySelector('[data-scroll-reveal-cards]')) continue;
    classify(section);
  }
  if (scrollTargets.length === 0 && loadTargets.length === 0 && !cardsMode) return;

  observer = new IntersectionObserver(
    (entries) => {
      let sectionIndex = 0;
      let cardIndex = 0;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer?.unobserve(entry.target);
        if (entry.target.classList.contains('shopify-section')) {
          reveal(entry.target, sectionIndex * STAGGER_MS, durationMs);
          sectionIndex += 1;
        } else {
          reveal(entry.target, cardIndex * CARD_STAGGER_MS, durationMs);
          cardIndex += 1;
        }
      }
    },
    { rootMargin: `0px 0px -${Math.round((1 - FOLD_LINE) * 100)}% 0px` }
  );

  for (const target of scrollTargets) {
    target.classList.add(HIDDEN_CLASS);
    observer.observe(target);
  }

  /* Page-load entrance: everything above the fold cascades in top-to-bottom.
     Hide first, flush styles so the hidden state is committed, then reveal —
     without the flush both classes land in one frame and nothing transitions. */
  if (loadTargets.length > 0) {
    loadTargets.sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    );
    for (const target of loadTargets) target.classList.add(HIDDEN_CLASS);
    void document.body.offsetHeight;
    loadTargets.forEach((target, index) => {
      reveal(target, Math.min(index * LOAD_STAGGER_MS, LOAD_DELAY_MAX_MS), durationMs);
    });
  }

  /* Infinite scroll / "load more": cards appended to a tagged grid after init
     join the scroll reveal when they land below the fold. Storefront only — in
     the editor, section re-renders would race the neutralise handler. */
  if (grids.length > 0 && !window.Shopify?.designMode) {
    const appendWatcher = new MutationObserver((mutations) => {
      const line = window.innerHeight * FOLD_LINE;
      /* Reads (rects) batched before writes (classes) to avoid layout thrash
         when a whole page of cards lands at once. */
      const added = [];
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.hasAttribute('data-scroll-reveal-skip')) continue;
          if (node.classList.contains(HIDDEN_CLASS) || node.classList.contains(IN_CLASS)) continue;
          added.push(node);
        }
      }
      const eligible = added.filter((node) => {
        const rect = node.getBoundingClientRect();
        return (rect.width > 0 || rect.height > 0) && rect.top > line;
      });
      for (const node of eligible) {
        node.classList.add(HIDDEN_CLASS);
        observer?.observe(node);
      }
    });
    for (const grid of grids) appendWatcher.observe(grid, { childList: true });
  }
}

init();

/* Theme editor: the effect runs so merchants can preview it by scrolling, but a
   section the editor re-renders (any setting tweak fires shopify:section:load)
   is taken out of the game — no replayed entrance on every tweak, and a
   still-hidden section can never come back invisible after an edit. */
if (window.Shopify?.designMode) {
  const neutralise = (/** @type {Event} */ event) => {
    const target = /** @type {Element} */ (event.target);
    const section = target.closest?.('.shopify-section') || target;
    if (!(section instanceof Element)) return;
    observer?.unobserve(section);
    cleanup(section);
    for (const card of section.querySelectorAll(`.${HIDDEN_CLASS}, .${IN_CLASS}`)) {
      observer?.unobserve(card);
      cleanup(card);
    }
  };
  document.addEventListener('shopify:section:load', neutralise);
  document.addEventListener('shopify:section:select', neutralise);
  document.addEventListener('shopify:block:select', neutralise);
}
